import { useEffect, useRef } from "react"
import { renderToString } from "react-dom/server"
import { useBaseMap } from "../BaseMap"
import Technician from "../../../modules/technicians/models/technician"
import Job from "../../../modules/crm/jobs/models/Job"
import { techMarkerIcon } from "../TechMarker"
import TechMapPopup from "../TechMapPopup"
import { markerSizeForZoom, resolveTechPosition } from "../mapUtils"
import { HereGeocodePosition } from "../../../models/MapModels"
import { useJobs, useSocket } from "../../../core/Global"
import {
    applyTechDeviceState, applyTechLive, isTechLive, onTechClock, onTechDeviceState, techClock,
    techLivePoint, TechMapLocationUpdate, watchTechClocks
} from "../../../core/TechLive"

interface IProps {
    techs: Technician[]
    /** Ringed in the brand blue: the one being picked. */
    selectedTechId?: string
    /** Pointed at from outside the map (a row of a list being hovered): larger and on top. */
    hoveredTechId?: string
    /** Stepped back: on the map but not a choice here. */
    dimmedTechIds?: Set<string>
    /** Follow the phones: move with every reported position, ring the live ones, grey those off the clock. */
    live?: boolean
    onTechClick?: (tech: Technician) => void
}

/**
 * Technicians as their own faces, where resolveTechPosition says they are. Hovering one opens the
 * board's panel about them; it lingers a moment so it can be read.
 */
export default function TechMarkersLayer(props: IProps) {

    const { L, map, zoom } = useBaseMap()
    const socket = useSocket()
    const jobs = useJobs()

    const size = markerSizeForZoom(zoom)
    const markersRef = useRef<Record<string, any>>({})
    const popupCloseTimer = useRef<number>()

    const onTechClick = useRef(props.onTechClick)
    onTechClick.current = props.onTechClick
    const jobsRef = useRef<Job[] | undefined>(jobs)
    jobsRef.current = jobs

    const inProgressJob = (techId?: string) => (jobsRef.current ?? []).find(j => j.techId === techId && j.isInProgress === true)

    const popupFor = (tech: Technician) => {
        const resolved = resolveTechPosition(tech)
        const shown: Technician = resolved && resolved.source !== "wakeup"
            ? { ...tech, techLocation: resolved.position, techLocationLastUpdate: resolved.at }
            : tech
        return renderToString(
            <TechMapPopup tech={shown}
                jobs={(jobsRef.current ?? []).filter(job => job.techId === tech.id)}
                isWakeupAddress={resolved?.source === "wakeup"} />
        )
    }

    // Live, off the clock, pointed at, stepped back: classes on the marker, not a new icon.
    const refreshClasses = (techId: string) => {
        const marker = markersRef.current[techId]
        const el = marker?.getElement?.()
        if (!el) return
        if (props.live) {
            el.classList.toggle("tech-marker-live", isTechLive(techId, techLivePoint(techId)?.at ?? marker.options.tech?.techLocationLastUpdate))
            el.classList.toggle("tech-marker-offline", !techClock(techId)?.on)
        }
        el.classList.toggle("is-hovered", techId === hoveredRef.current)
        el.classList.toggle("is-dimmed", !!dimmedRef.current?.has(techId))
    }

    const hoveredRef = useRef(props.hoveredTechId)
    hoveredRef.current = props.hoveredTechId
    const dimmedRef = useRef(props.dimmedTechIds)
    dimmedRef.current = props.dimmedTechIds

    // The markers. Rebuilt when the list, the size or the pick changes -- a few dozen markers,
    // and a rebuilt icon is the only way to change its face.
    useEffect(() => {
        const markers: Record<string, any> = {}

        props.techs.forEach(tech => {
            if (!tech.id) return
            const resolved = resolveTechPosition(tech)
            if (!resolved) return
            const techId = tech.id

            const marker = L.marker([resolved.position.lat, resolved.position.lng], {
                icon: techMarkerIcon(L, tech, { busy: !!inProgressJob(techId), size, selected: techId === props.selectedTechId }),
                keyboard: false,
                zIndexOffset: techId === props.selectedTechId ? 1000 : 0,
                tech
            })

            marker.bindPopup("", { closeButton: false, autoPan: false, maxWidth: 320, minWidth: 250, className: "tech-map-popup-wrap" })
            marker.on("mouseover", () => {
                window.clearTimeout(popupCloseTimer.current)
                marker.setPopupContent(popupFor(tech))
                marker.openPopup()
            })
            marker.on("mouseout", () => {
                popupCloseTimer.current = window.setTimeout(() => marker.closePopup(), 260)
            })
            marker.on("click", () => {
                marker.closePopup()
                onTechClick.current?.(tech)
            })

            marker.addTo(map)
            markers[techId] = marker
        })

        markersRef.current = markers
        Object.keys(markers).forEach(refreshClasses)

        return () => {
            window.clearTimeout(popupCloseTimer.current)
            Object.values(markers).forEach(m => m.remove())
            markersRef.current = {}
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, props.techs, size, props.selectedTechId])

    // Pointing and dimming change classes only.
    useEffect(() => {
        Object.keys(markersRef.current).forEach(id => {
            refreshClasses(id)
            const m = markersRef.current[id]
            if (id === props.hoveredTechId) m.setZIndexOffset(3000)
            else m.setZIndexOffset(id === props.selectedTechId ? 1000 : 0)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.hoveredTechId, props.dimmedTechIds])

    // Following the phones. Points arrive over the socket and move the markers in place. Taken
    // off by handler, not by event name: the jobs board's own map listens to the same events.
    useEffect(() => {
        if (!props.live) return

        const apply = (techId: string, position: HereGeocodePosition, at?: Date | string) => {
            if (!techId || !position?.lat || !position?.lng) return
            applyTechLive(techId, position, at)
            const marker = markersRef.current[techId]
            if (!marker) return
            marker.setLatLng([position.lat, position.lng])
            refreshClasses(techId)
            const el = marker.getElement?.()
            if (el && techClock(techId)?.on) {
                el.classList.remove("tech-marker-reported")
                void el.offsetWidth
                el.classList.add("tech-marker-reported")
            }
            if (marker.isPopupOpen?.()) marker.setPopupContent(popupFor(marker.options.tech))
        }

        const onBatch = (batch: TechMapLocationUpdate[]) => (batch ?? []).forEach(item => {
            if (item.batteryLevel !== undefined && item.batteryLevel !== null) {
                applyTechDeviceState(item.techId, { batteryLevel: item.batteryLevel, charging: item.charging, batteryAt: item.at })
            }
            if (item.appState) applyTechDeviceState(item.techId, { appState: item.appState, appStateAt: item.at })
            apply(item.techId, item.position, item.at)
        })
        const onOne = (techId: string, pos: HereGeocodePosition, at?: string) => apply(techId, pos, at)

        const key = socket.start(
            skt => {
                skt.on("TechMapLocationsUpdate", onBatch)
                skt.on("TechMapLocationUpdate", onOne)
            },
            skt => {
                skt.off("TechMapLocationsUpdate", onBatch)
                skt.off("TechMapLocationUpdate", onOne)
            }
        )

        const refreshPopup = (techId: string) => {
            const m = markersRef.current[techId]
            if (m?.isPopupOpen?.()) m.setPopupContent(popupFor(m.options.tech))
        }

        watchTechClocks()
        const stopClock = onTechClock(techId => { refreshClasses(techId); refreshPopup(techId) })
        const stopDevice = onTechDeviceState(techId => refreshPopup(techId))
        // "Live" ages out without any event.
        const sweep = window.setInterval(() => Object.keys(markersRef.current).forEach(refreshClasses), 20000)

        return () => {
            window.clearInterval(sweep)
            stopClock()
            stopDevice()
            socket.stop(key)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, props.live])

    return null
}
